import React, { useCallback } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { InferType } from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { useQuery } from '@tanstack/react-query'
import { Button, ButtonVariant } from '../../../common/Button'
import { useAppTranslation } from '../../../../hooks/useAppTranslation'
import { TextInput } from '../../../common/TextInput'
import { useFormErrorMessage } from '../../../../hooks/useFormErrorMessage'
import { FormControl } from '../../../common/FormControl'
import { unitCreateSchema } from '../../../../forms/schemas/society_unit_create'
import { SelectDropdown } from '../../../common/SelectDropdown'
import { getFloorFromTitleLegal } from '../../../../helpers/units'
import { IUnitModel } from '../../../../interfaces/models/units.interfaces'
import { ISocietiesModel } from '../../../../interfaces/models/societies.interfaces'
import { getIsSamf } from '../../../../state/models/society'
import { entranceQueries } from '../../../../api/society-entrances'
import { i18nSocietyType } from '../../../../i18n'

interface CreateUpdateRegisterUnitItemFormProps {
  onError: () => void
  onSubmit: (data: InferType<typeof unitCreateSchema>) => Promise<void>
  onClose: () => void
  loading: boolean
  unit?: IUnitModel
  society: ISocietiesModel
}

export const CreateUpdateRegisterUnitItemForm = ({
  unit,
  loading,
  onError,
  onSubmit,
  onClose,
  society,
}: CreateUpdateRegisterUnitItemFormProps): React.ReactNode => {
  const { translate } = useAppTranslation()
  const { getErrorMessage } = useFormErrorMessage()

  const updateMode = !!unit

  const isSamf = getIsSamf(society)

  const isNeighbourhood = society?.societyTypeDisplayName === 'neighbourhood'

  const getDefaultValues = useCallback((): InferType<
    typeof unitCreateSchema
  > => {
    return {
      entranceId: updateMode ? unit?.entranceId || '' : '',
      title: updateMode ? unit?.title || '' : '',
      titleLegal: updateMode ? unit?.titleLegal || '' : '',
      floor: updateMode ? unit?.floor : 0,
      societyId: updateMode ? unit?.societyId || society?._id : society?._id,
    }
  }, [society, unit, updateMode])

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    setValue,
  } = useForm({
    mode: 'all',
    resolver: yupResolver(unitCreateSchema),
    defaultValues: getDefaultValues(),
  })

  const { data: societyEntrances } = useQuery(
    entranceQueries.getEntrancesPerSociety(society._id)
  )

  const entranceOptions =
    societyEntrances?.map((_entrance) => ({
      label: _entrance.addressStreet as string,
      value: _entrance._id,
    })) || []

  return (
    <>
      <Controller
        control={control}
        name="entranceId"
        render={({ field: { value, name, onChange } }) => (
          <FormControl
            label={translate('registerView.unit.labels.entrance', {
              context: i18nSocietyType(society.societyTypeDisplayName),
            })}
            error={errors.entranceId && getErrorMessage(errors.entranceId)}
            name={name}
          >
            <SelectDropdown
              options={entranceOptions}
              value={entranceOptions.find((option) => option.value === value)}
              onChange={(option) => onChange(option?.value)}
              error={!!errors.entranceId}
            />
          </FormControl>
        )}
      />
      <Controller
        control={control}
        name="titleLegal"
        render={({ field: { value, name, onChange, onBlur } }) => (
          <FormControl
            label={translate('registerView.unit.labels.titleLegal', {
              context: i18nSocietyType(society.societyTypeDisplayName),
            })}
            error={errors.titleLegal && getErrorMessage(errors.titleLegal)}
            name={name}
          >
            <TextInput
              value={value}
              onChange={(e) => {
                onChange(e)
                if (!isSamf && !isNeighbourhood) {
                  setValue(
                    'floor',
                    getFloorFromTitleLegal((e.target as HTMLInputElement).value)
                  )
                }
              }}
              onBlur={onBlur}
              error={!!errors.titleLegal}
            />
          </FormControl>
        )}
      />
      <Controller
        control={control}
        name="title"
        render={({ field: { value, name, onChange, onBlur } }) => (
          <FormControl
            label={translate('registerView.unit.labels.title', {
              context: i18nSocietyType(society.societyTypeDisplayName),
            })}
            error={errors.title && getErrorMessage(errors.title)}
            name={name}
          >
            <TextInput
              value={value}
              onChange={onChange}
              onBlur={onBlur}
              error={!!errors.title}
            />
          </FormControl>
        )}
      />

      {!isSamf && !isNeighbourhood && (
        <Controller
          control={control}
          name="floor"
          render={({ field: { value, name, onChange, onBlur } }) => (
            <FormControl
              label={translate('registerView.unit.labels.floor')}
              helperText={translate(
                'common.form.helperTexts.calculatedAutomatically'
              )}
              error={errors.floor && getErrorMessage(errors.floor)}
              name={name}
              disabled
            >
              <TextInput
                disabled
                value={`${value}`}
                onChange={onChange}
                onBlur={onBlur}
                error={!!errors.floor}
              />
            </FormControl>
          )}
        />
      )}

      <div className="flex flex-shrink-0 flex-wrap items-center justify-end gap-4">
        <Button label={translate('common.actions.cancel')} onClick={onClose} />
        <Button
          disabled={!isValid || loading}
          variant={ButtonVariant.PRIMARY}
          label={
            updateMode
              ? translate('common.actions.save')
              : translate('common.actions.create')
          }
          type="submit"
          onClick={handleSubmit(onSubmit, onError)}
        />
      </div>
    </>
  )
}
